/**
 * It also knows when not to answer (§23).
 *
 * The part that stops the product reading as "replace your staff", which is
 * both untrue and the thing a salon owner is quietly worried about.
 *
 * It closes « Un vrai travail de réception » rather than opening a section of
 * its own: « Transférer » is the sixth of the jobs listed there, and this is
 * what that job means. As a separate section it was one more heading and one
 * more screen on a page that was already ten screens long.
 *
 * The confidence meter is an SVG with its geometry in **attributes**. A
 * `style="width:41%"` would be dropped by the browser under this deployment's
 * `style-src 'self'`, and the bar would silently render empty — the kind of
 * fault that looks like a design choice. The figure is also written out in
 * text beside it, because a bar is not readable by everyone (§44).
 */

const REASONS: readonly string[] = [
  'La demande n’est pas claire, même après une question de clarification.',
  'Le client demande à parler à quelqu’un.',
  'La demande sort de ce que le salon prend par téléphone.',
  'Le sujet est délicat : une réclamation, un incident, un imprévu.',
];

export function Handover(): React.JSX.Element {
  return (
    <div className="mt-12 sm:mt-14">
      <h3 className="sign text-sign-sm text-ink">Elle sait aussi quand ne pas répondre.</h3>
      <p className="mt-2 max-w-2xl text-ui text-ink-muted">
        Un transfert au bon moment est un bon résultat, pas un échec. Le tableau de bord les compte
        comme tels.
      </p>

      <div className="mt-6 grid gap-8 xl:grid-cols-2 xl:gap-10">
        <div>
          <p className="text-ui font-medium text-ink">Elle passe la main quand :</p>
          <ul className="mt-2 border-b border-wall-line">
            {REASONS.map((reason) => (
              <li key={reason} className="border-t border-wall-line py-3.5 text-ui text-ink">
                {reason}
              </li>
            ))}
          </ul>
        </div>

        <figure className="rounded-[14px] border border-wall-line bg-surface p-5 sm:p-6">
          <figcaption className="text-ui-sm text-ink-muted">Exemple d’appel</figcaption>

          <blockquote className="mt-3 text-subtitle text-ink">
            « Je voudrais quelque chose pour mes cheveux, je ne sais pas trop quoi. »
          </blockquote>

          <div className="mt-6">
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-ui-sm text-ink-muted">Confiance</span>
              <span className="sign-figure text-ui text-ink">41 %</span>
            </div>
            <svg
              viewBox="0 0 100 6"
              preserveAspectRatio="none"
              role="img"
              aria-label="Niveau de confiance : 41 pour cent"
              className="mt-2 h-1.5 w-full"
            >
              <rect x="0" y="0" width="100" height="6" rx="3" fill="var(--surface-muted)" />
              <rect x="0" y="0" width="41" height="6" rx="3" fill="var(--text-muted)" />
            </svg>
          </div>

          <ol className="mt-6 space-y-2 text-ui-sm text-ink">
            <li className="flex gap-3">
              <span className="figures text-ink-muted">1.</span>
              Une question de clarification, une seule.
            </li>
            <li className="flex gap-3">
              <span className="figures text-ink-muted">2.</span>
              Toujours pas clair : transfert vers le salon.
            </li>
          </ol>

          <p className="mt-6 border-t border-wall-line pt-4 text-ui-sm text-ink-muted">
            L’assistante ne devine pas une prestation. Un rendez-vous pris au hasard coûte plus cher
            qu’un appel transféré.
          </p>
        </figure>
      </div>
    </div>
  );
}
